package com.essntl.features.crm.ui.components

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsHoveredAsState
import androidx.compose.foundation.interaction.collectIsPressedAsState
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.focusProperties
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.PointerIcon
import androidx.compose.ui.input.pointer.pointerHoverIcon
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.DpSize
import androidx.compose.ui.unit.dp
import com.essntl.core.unsplash.ui.UnsplashImage
import com.essntl.features.crm.domain.model.ContactOrganizationModel
import com.essntl.features.crm.domain.model.OrganizationListModel
import com.essntl.features.crm.ui.CRMNavigation
import com.essntl.features.crm.ui.CRMState
import com.essntl.uikit.components.file.FileViewer
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.icons.EsSmallIcon
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography
import kotlin.collections.lastIndex

@Composable
fun OrganizationItem(
    organizationListModel: OrganizationListModel,
    state: CRMState,
    onNavigation: (CRMNavigation) -> Unit,
    index: Int,
    modifier: Modifier = Modifier,
) {
    var isExpanded by rememberSaveable(organizationListModel.id) {
        mutableStateOf(organizationListModel.expanded)
    }

    val interactionSource = remember { MutableInteractionSource() }

    val isHovered by interactionSource.collectIsHoveredAsState()

    val isPressed by interactionSource.collectIsPressedAsState()

    var fileUrl by rememberSaveable { mutableStateOf("") }
    var isFileViewerOpened by rememberSaveable { mutableStateOf(false) }

    Column(
        modifier = modifier,
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            modifier =
                Modifier
                    .background(
                        when {
                            isPressed ->
                                MaterialTheme.esColorScheme.tintedPressed

                            isHovered ->
                                MaterialTheme.esColorScheme.tintedHover

                            else ->
                                Color.Transparent
                        },
                    )
                    .padding(
                        vertical = EsConstants.ListRowVerticalPadding,
                        horizontal = EsConstants.ListRowHorizontalPadding,
                    )
                    .height(80.dp)
                    .pointerHoverIcon(PointerIcon.Hand)
                    .focusProperties { canFocus = false }
                    .clickable(
                        interactionSource = interactionSource,
                        indication = null,
                        onClick = {
                            onNavigation(
                                CRMNavigation.OrganizationDetails(
                                    organizationListModel.id,
                                ),
                            )
                        },
                    ),
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier =
                    Modifier
                        .weight(4f)
                        .fillMaxHeight(),
            ) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    if (organizationListModel.contacts.isNotEmpty())
                        EsSmallIcon(
                            res = if (isExpanded)
                                EsIcons.ArrowIconDown else
                                EsIcons.ArrowIconRight,
                            tint = MaterialTheme.esColorScheme.iconDisabled,
                            modifier =
                                Modifier
                                    .clip(RoundedCornerShape(5.dp))
                                    .focusProperties { canFocus = false }
                                    .clickable {
                                        isExpanded = !isExpanded
                                    }
                                    .padding(.3.spacing),
                        )

                    Spacer(modifier = Modifier.width(1f.spacing))

                    SearchHighlightedText(
                        text = organizationListModel.name,
                        query = state.searchText,
                        textStyle = MaterialTheme.esTypography.s2SemiBold16,
                        textColor = MaterialTheme.esColorScheme.textPrimary,
                    )

                    if (organizationListModel.contacts.isNotEmpty()) {
                        Spacer(modifier = Modifier.width(1f.spacing))

                        Text(
                            text = "${organizationListModel.contacts.size}",
                            color = MaterialTheme.esColorScheme.textTertiary,
                            style = MaterialTheme.esTypography.p1Regular16,
                        )

                        Spacer(modifier = Modifier.width(.5f.spacing))

                        EsSmallIcon(res = EsIcons.Person)
                    }
                }
            }

            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier =
                    Modifier
                        .weight(2f)
                        .fillMaxHeight(),
            ) {
                organizationListModel.logoUrl?.let { url ->
                    UnsplashImage(
                        url = "$url?width=130&height=80",
                        imageSize = DpSize(130.dp, 80.dp),
                        selectable = false,
                        deletable = false,
                        bordered = false,
                        onClick = {
                            isFileViewerOpened = true
                            fileUrl = url
                        },
                    )
                }

                Spacer(modifier = Modifier.weight(1f))
            }

            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier =
                    Modifier
                        .weight(2f)
                        .fillMaxHeight(),
            ) {
                Text(
                    text = organizationListModel.website.orEmpty(),
                    color = MaterialTheme.esColorScheme.textPrimary,
                    style = MaterialTheme.esTypography.s2SemiBold16,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )

                Spacer(modifier = Modifier.weight(1f))
            }
        }
    }

    AnimatedVisibility(isExpanded) {
        ContactOrganizationColumn(
            contactsOrganizations = organizationListModel.contacts,
            onNavigation = onNavigation,
            searchText = state.searchText,
        )
    }

    if (index != state.getOrganizationList.data.orEmpty().lastIndex)
        HorizontalDivider(
            thickness = 1.dp,
            color = MaterialTheme.esColorScheme.borderPrimary,
            modifier = Modifier.fillMaxWidth(),
        )

    FileViewer(
        fileUrl = fileUrl,
        isDialogOpened = isFileViewerOpened,
        onDismissRequest = {
            isFileViewerOpened = false
            fileUrl = ""
        },
    )
}


@Composable
private fun ContactOrganizationColumn(
    contactsOrganizations: List<ContactOrganizationModel>,
    onNavigation: (CRMNavigation) -> Unit,
    searchText: String,
) {
    if (contactsOrganizations.isEmpty()) {
        Box(
            contentAlignment = Alignment.Center,
            modifier = Modifier.fillMaxWidth().height(60.dp),
        ) {
            Text(
                text = "No contact or organization found!",
                color = MaterialTheme.esColorScheme.textSecondary,
                style = MaterialTheme.esTypography.p1Medium16,
            )
        }
    } else {
        Column(
            verticalArrangement = Arrangement.spacedBy(1.spacing),
        ) {
            HorizontalDivider(
                thickness = 1.dp,
                color = MaterialTheme.esColorScheme.borderPrimary,
            )

            contactsOrganizations
                .forEachIndexed { index, contactOrganization ->
                    key(contactOrganization.id) {
                        ContactOrganizationItem(
                            contactOrganization = contactOrganization,
                            onNavigation = onNavigation,
                            showDivider = index != contactsOrganizations.lastIndex,
                            searchText = searchText,
                            modifier = Modifier.fillMaxWidth(),
                        )
                    }
                }
        }
    }
}